import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, subscriptionWrite, type createAccountApi } from '../../api/client';
import type { Envelope, User } from '../../api/contract';
import { Dialog } from '../../shared/ui/Dialog';
import { publishAuthChange } from '../auth-context';
import { clearPurchaseIntent, readPurchaseIntent, savePurchaseIntent, type Purchase, type PurchaseIntent, type Subscription } from './model';
import type { SubscriptionState } from './useSubscription';
import './subscription.css';

export function SubscriptionPage({ api, user, state }: { api: ReturnType<typeof createAccountApi>; user: User; state: SubscriptionState }) {
  const owner = { userId: user.id, origin: window.location.origin, epoch: api.epoch };
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const [intent, setIntent] = useState(() => readPurchaseIntent(owner));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [accountPending, setAccountPending] = useState(false);
  const accountBusy = useRef(false);
  const busy = useRef(false);
  const trialKey = useRef(crypto.randomUUID());
  const active = useRef(true);
  const snapshot = state.snapshot;
  const persist = (next: PurchaseIntent) => { api.assertCurrent(); savePurchaseIntent(next); setIntent(next); };
  const adopt = (next: Purchase) => {
    api.assertCurrent();
    if (!active.current) return;
    setPurchase(next);
    if (next.status !== 'PENDING') { clearPurchaseIntent(owner); setIntent(undefined); }
  };
  useEffect(() => {
    active.current = true;
    document.title = 'Подписка — Казна';
    return () => { active.current = false; };
  }, []);
  useEffect(() => {
    if (!snapshot || !snapshot.enabled) return;
    let current = true;
    let acquired = false;
    const recover = async () => {
      // Reconcile external storage only after setup, so StrictMode's abandoned
      // effect cannot acquire the checkout or leave the current page busy.
      await Promise.resolve();
      if (!current) return;
      api.assertCurrent();
      const owned = readPurchaseIntent({ userId: user.id, origin: window.location.origin, epoch: api.epoch });
      if (snapshot.pendingPurchase) {
        setPurchase(snapshot.pendingPurchase);
        const next = { ...(owned ?? { userId: user.id, origin: window.location.origin, epoch: api.epoch, key: crypto.randomUUID() }), purchaseId: snapshot.pendingPurchase.id };
        savePurchaseIntent(next); setIntent(next);
      }
      if (!owned || busy.current) return;
      busy.current = true; acquired = true; setPending(true);
      const next = owned.purchaseId
        ? (await api.apiGet<Envelope<Purchase>>(`/me/subscription/purchases/${owned.purchaseId}`)).data
        : (await subscriptionWrite<Envelope<Purchase>>('/me/subscription/purchases', { offerId: snapshot.offer.id }, owned.key, api.epoch)).data;
      api.assertCurrent();
      if (!current) return;
      if (next.status === 'PENDING') {
        const saved = { ...owned, purchaseId: next.id }; savePurchaseIntent(saved); setIntent(saved);
      } else { clearPurchaseIntent(owned); setIntent(undefined); }
      setPurchase(next);
      if (next.status !== 'PENDING') { setPending(false); busy.current = false; void state.refresh().catch(() => undefined); }
    };
    void recover().catch(caught => { if (current) setError(caught instanceof Error ? caught.message : 'Не удалось проверить оплату.'); })
      .finally(() => { if (acquired) { busy.current = false; if (active.current) setPending(false); } });
    return () => { current = false; if (acquired) busy.current = false; };
    // A new authoritative snapshot reconciles the same stored intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, snapshot, user.id]);

  const run = async (work: () => Promise<void>) => {
    if (busy.current) return;
    busy.current = true; setPending(true); setError('');
    try { await work(); }
    catch (caught) { if (active.current) setError(caught instanceof Error ? caught.message : 'Не удалось подтвердить результат. Повторите действие.'); }
    finally { busy.current = false; if (active.current) setPending(false); }
  };
  const runAccount = async (work: () => Promise<void>) => {
    if (accountBusy.current) return;
    accountBusy.current = true; setAccountPending(true); setError('');
    try { await work(); }
    catch (caught) { if (active.current) setError(caught instanceof Error ? caught.message : 'Не удалось выполнить действие с аккаунтом.'); }
    finally { accountBusy.current = false; if (active.current) setAccountPending(false); }
  };
  const startTrial = () => run(async () => {
    const requestedAt = performance.now();
    const result = await subscriptionWrite<Envelope<Subscription>>('/me/subscription/trial', {}, trialKey.current, api.epoch);
    api.assertCurrent(); await state.accept(result.data, requestedAt);
  });
  const createPurchase = () => run(async () => {
    if (!snapshot) return;
    const saved = readPurchaseIntent(owner) ?? { ...owner, key: crypto.randomUUID() };
    persist(saved);
    const result = await subscriptionWrite<Envelope<Purchase>>('/me/subscription/purchases', { offerId: snapshot.offer.id }, saved.key, api.epoch);
    api.assertCurrent(); persist({ ...saved, purchaseId: result.data.id }); adopt(result.data);
  });
  const confirm = (outcome: 'success' | 'failure' | 'cancel') => run(async () => {
    if (!purchase || purchase.status !== 'PENDING') return;
    const saved = readPurchaseIntent(owner) ?? { ...owner, key: crypto.randomUUID(), purchaseId: purchase.id };
    const attempt = { ...saved, outcome: saved.outcome ?? outcome, confirmKey: saved.confirmKey ?? crypto.randomUUID() };
    persist(attempt);
    const requestedAt = performance.now();
    const result = await subscriptionWrite<Envelope<{ purchase: Purchase; subscription: Subscription }>>(`/me/subscription/purchases/${purchase.id}/mock-confirm`, { outcome: attempt.outcome }, attempt.confirmKey, api.epoch);
    api.assertCurrent(); adopt(result.data.purchase); await state.accept(result.data.subscription, requestedAt);
  });
  const logout = () => runAccount(async () => {
    await api.apiMutation('/auth/web/logout', { scope: 'web-auth:logout' });
    window.location.assign('/login');
  });
  const removeAccount = () => runAccount(async () => {
    try { await api.apiMutation('/me', { method: 'DELETE', scope: `account:delete:${user.id}` }); }
    catch (caught) {
      if (caught instanceof ApiError && caught.code === 'OWNED_WORKSPACES_EXIST') throw new Error('Сначала удалите свои пространства или передайте владение в разделе «Пространства и удаление».');
      throw caught;
    }
    api.assertCurrent(); clearPurchaseIntent(owner);
    // Close every tab and discard in-flight account data before returning to
    // public UI. A pending deletion cannot retain the former financial cache.
    publishAuthChange();
    window.location.assign('/auth/email/login?accountDeleted=1');
  });
  const end = snapshot?.accessUntil ? new Intl.DateTimeFormat('ru-RU', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(snapshot.accessUntil)) : null;
  const terminalText = purchase ? { SUCCEEDED: 'Тестовая оплата прошла. Доступ продлён.', FAILED: 'Тестовая оплата отклонена. Можно попробовать снова.', CANCELLED: 'Тестовая оплата отменена.', EXPIRED: 'Время тестовой оплаты истекло.' }[purchase.status as Exclude<Purchase['status'], 'PENDING'>] : undefined;
  return <main className="subscription-page">
    <header><img src="/icon.svg" width="40" height="40" alt="" /><h1>Подписка Казны</h1><p>{user.displayName}</p></header>
    <>
      <article className="card subscription-card">
        {!snapshot && !state.error && !state.loading && <p>Подписка пока не требуется.</p>}
        <h2>{state.allowed ? 'Все возможности доступны' : snapshot?.canStartTrial ? 'Попробуйте Казну' : 'Продолжайте вести бюджет'}</h2>
        <p className="subscription-price">590 ₽ <span>/ месяц за пользователя</span></p>
        <p>Одна подписка для всех ваших пространств. Каждый участник семьи оформляет её для себя.</p>
        {end && <p>Доступ до {end}.</p>}
        {snapshot?.canStartTrial && <><p>14 дней бесплатно, без карты. Пробный период доступен один раз для аккаунта.</p><button type="button" className="button button-primary" disabled={pending} onClick={() => void startTrial()}>Начать 14 дней бесплатно</button></>}
        {state.allowed && <Link className="button button-primary" to="/dashboard">Открыть Казну</Link>}
        {snapshot?.paymentMode === 'mock' && <>
          <p className="subscription-test" role="note">Тестовая оплата. Деньги не списываются.</p>
          {purchase?.status === 'PENDING' ? <section aria-label="Тестовая оплата">
            <p>Выберите результат тестовой оплаты. Банковская карта не нужна.</p>
            {intent?.outcome ? <button type="button" className="button button-primary" disabled={pending} onClick={() => void confirm(intent.outcome!)}>Проверить тот же результат</button> : <div className="subscription-actions">
              <button type="button" className="button button-primary" disabled={pending} onClick={() => void confirm('success')}>Успешная оплата</button>
              <button type="button" className="button button-quiet" disabled={pending} onClick={() => void confirm('failure')}>Отклонить оплату</button>
              <button type="button" className="button button-quiet" disabled={pending} onClick={() => void confirm('cancel')}>Отменить оплату</button>
            </div>}
          </section> : <button type="button" className="button button-quiet" disabled={pending} onClick={() => void createPurchase()}>{intent ? 'Восстановить тестовую оплату' : state.allowed ? 'Продлить за 590 ₽' : 'Оформить за 590 ₽'}</button>}
        </>}
        {snapshot?.paymentMode === 'disabled' && <p>Оплата пока недоступна. Попробуйте позже.</p>}
        {terminalText && <p role="status">{terminalText}</p>}
        {pending && <p role="status" aria-live="polite">Проверяем результат…</p>}
        {state.loading && <p role="status">Проверяем подписку…</p>}
        {!deleteOpen && (error || state.error) && <p className="error" role="alert">{error || 'Не удалось проверить подписку. Финансовые данные скрыты.'}</p>}
        <button type="button" className="button button-quiet" disabled={pending} onClick={() => void run(state.refresh)}>Обновить статус</button>
      </article>
      <nav className="subscription-links" aria-label="Управление аккаунтом"><Link to="/account/profile">Профиль</Link><Link to="/account/sign-in-methods">Способы входа</Link><Link to="/account/sessions">Сессии и устройства</Link><Link to="/account/workspaces">Пространства и удаление</Link><button type="button" onClick={() => setDeleteOpen(true)}>Удалить аккаунт</button></nav>
      <details><summary>Как работает подписка</summary><p>После 14 дней пробного периода для финансовых экранов нужна личная подписка. Без неё можно управлять аккаунтом, выйти или запросить его удаление. Автоматических списаний нет.</p></details>
    </>
    <button type="button" className="button button-quiet" disabled={accountPending} onClick={() => void logout()}>Выйти из Казны</button>
    {deleteOpen && <Dialog variant="sheet" title="Удалить аккаунт?" manageFocus onClose={() => setDeleteOpen(false)}><p>Будет запрошено удаление аккаунта и ваших данных по правилам сервиса. Действие нельзя отменить после выполнения.</p>{error && <p className="error" role="alert">{error}</p>}<button type="button" className="button button-quiet" onClick={() => setDeleteOpen(false)}>Сохранить аккаунт</button><button type="button" className="button negative" disabled={accountPending} onClick={() => void removeAccount()}>Запросить удаление</button></Dialog>}
  </main>;
}
